import { Clock, Mail, ShieldCheck } from 'lucide-react';

import { buttonVariants } from '@/components/ui/button';
import { Column } from '@/components/ui/column';
import { Row } from '@/components/ui/row';
import { Text } from '@/components/ui/text';
import { contactEmail, contactEmailHref } from '@/constants/contact';
import { tv } from '@/lib/tv';
import { cn } from '@/lib/utils';

import GoodbyeSunset from './components/GoodbyeSunset';

const goodbyePageStyles = tv({
  slots: {
    root: 'h-full w-full overflow-y-auto bg-primary-beige-100 px-4 py-6 sm:px-8 sm:py-16',
    shell:
      'm-auto w-full max-w-[640px] shrink-0 overflow-clip rounded-[28px] bg-surface shadow-lg shadow-inner-border-warm',
    content: 'px-6 pb-10 pt-9 sm:px-12 sm:pb-12 sm:pt-12',
    heading: 'sm:text-[40px] sm:leading-[46px] tracking-[-0.01em]',
    body: 'max-w-[60ch] text-base leading-7',
    facts:
      'rounded-2xl bg-primary-beige-100 px-5 py-4 shadow-inner-border-warm',
    factIcon: 'mt-1.5 size-4 shrink-0 text-icon-secondary',
    action: 'focus-visible:focus-ring-brand pl-5 pr-6',
    signoff: 'border-t border-stroke-warm pt-6',
  },
});

const GoodbyePage = () => {
  const styles = goodbyePageStyles();

  return (
    <Column as="main" className={styles.root()}>
      <Column className={styles.shell()}>
        <GoodbyeSunset />

        <Column gap={8} className={styles.content()}>
          <Column gap={2}>
            <Text
              as="h1"
              variant="h1"
              wrap="balance"
              className={styles.heading()}
            >
              Thanks for being part of Rodeo.
            </Text>
            <Text as="p" variant="large" color="secondary">
              Rodeo has now closed.
            </Text>
          </Column>

          <Column gap={4}>
            <Text
              as="p"
              variant="body"
              weight="regular"
              wrap="pretty"
              className={styles.body()}
            >
              Building Rodeo has been a lot of fun, and we’re proud to have
              helped people progress in their careers.
            </Text>
            <Text
              as="p"
              variant="body"
              weight="regular"
              wrap="pretty"
              className={styles.body()}
            >
              We wish you all the best with what comes next.
            </Text>
          </Column>

          <Column as="ul" gap={2} className={styles.facts()}>
            <Row as="li" gap={3} align="start">
              <Clock aria-hidden className={styles.factIcon()} />
              <Text
                variant="body"
                weight="regular"
                wrap="pretty"
                className={styles.body()}
              >
                Accounts and job search tools stopped working
                at&nbsp;12:00&nbsp;on&nbsp;Friday&nbsp;16&nbsp;October.
              </Text>
            </Row>
            <Row as="li" gap={3} align="start">
              <ShieldCheck aria-hidden className={styles.factIcon()} />
              <Text
                variant="body"
                weight="regular"
                wrap="pretty"
                className={styles.body()}
              >
                Your data has been deleted.
              </Text>
            </Row>
          </Column>

          <Column gap={4}>
            <Text
              as="p"
              variant="body"
              weight="regular"
              wrap="pretty"
              className={styles.body()}
            >
              If you have any questions, you can still reach us.
            </Text>
            <Row gap={4} align="center" wrap="wrap">
              <a
                className={cn(buttonVariants({ size: 'lg' }), styles.action())}
                href={contactEmailHref}
              >
                <Mail aria-hidden />
                Get in touch
              </a>
              <Text
                variant="body"
                weight="regular"
                color="secondary"
                className={styles.body()}
              >
                {contactEmail}
              </Text>
            </Row>
          </Column>

          <Column gap={1} className={styles.signoff()}>
            <Text
              as="p"
              variant="body"
              weight="regular"
              color="secondary"
              className={styles.body()}
            >
              With thanks,
            </Text>
            <Text as="p" variant="h3">
              Alfie, Budde, Lee and Will
            </Text>
          </Column>
        </Column>
      </Column>
    </Column>
  );
};

export default GoodbyePage;
